import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stack, Typography, Grid, Alert, Box, IconButton, Snackbar, Divider, Skeleton, Button } from '@mui/material';
import api from '../Services/Api';
import { useNavigate, useLocation } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import Loading from '../components/Loading';
import MailOutlined from '@mui/icons-material/MailOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import SearchComponent from '../components/SearchComponent';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MicIcon from '@mui/icons-material/Mic';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import WebIcon from '@mui/icons-material/Web';
import InboxIcon from '@mui/icons-material/Inbox';
import dayjs from 'dayjs';
import Recorder from '../components/Recorder';
import ProcessingComponent from '../designsystem/ProcessingComponent';
import FlowContextChip from '../record/components/FlowContextChip';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import {
    glassCard, ACCENT, ACCENT_DARK, ACCENT_GLOW,
    TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, TEXT_HINT,
    SURFACE_BORDER, SURFACE_HOVER,
    TYPE_SCALE, noMotion
} from '../designsystem/tokens';

const Contacts = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const [loading, setLoading] = useState(true);
    const crmUpdated = state?.ok ?? false;
    const [filteredContacts, setFilteredContacts] = useState([]);
    const previousController = useRef();
    const [createdate, setCreatedate] = useState("DESCENDING");

    const navigate = useNavigate();
    useEffect(() => {
        api.fetchContacts({ firstName: undefined, after: 0, createdate: createdate, noCache: true }).then((response) => {
            setFilteredContacts(response.options || []);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching contacts", error);
            setLoading(false);
        });
    }, []);

    const handleSearchChange = (text) => {
        if (previousController.current) {
            previousController.current.abort();
        }
        const controller = new AbortController();
        const signal = controller.signal;
        previousController.current = controller;
        setLoading(true);

        api.fetchContacts({ firstName: text.toLowerCase(), after: 0, createdate: createdate, signal: signal }).then((response) => {
            setFilteredContacts(response.options || []);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching contacts", error);
            setLoading(false);
        });
    };

    const handleSortChange = (searchTerm, createdate) => {
        setLoading(true);
        setCreatedate(createdate);
        api.fetchContacts({ firstName: searchTerm, after: 0, createdate: createdate }).then((response) => {
            setFilteredContacts(response.options || []);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching contacts", error);
            setLoading(false);
        });
    }

    return (
        <Stack style={{padding: '1rem', maxWidth: '100%', margin: 'auto' }}>
            <Stack direction="row" spacing={2} style={{marginBottom: '1rem'}} alignItems="center">
                <IconButton aria-label={t('goBack', 'Go back')} style={{ color: 'white'}} onClick={() => {
                    navigate("/app");
                }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" style={{color: 'white', textAlign: 'left', width: '100%', verticalAlign: 'middle'}} >{t('contacts')}</Typography>
            </Stack>
            <Loading loading={loading} />
            <Snackbar open={crmUpdated} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert icon={<CheckIcon style={{ color: "white" }} fontSize="inherit" />} sx={{ borderRadius: '12px', backgroundColor: '#366E6B', color: 'white' }}>
                    {t('crmUpdated')}
                </Alert>
            </Snackbar>
           <SearchComponent createdate={createdate} handleSortChange={handleSortChange} handleSearchChange={handleSearchChange} />

            {!loading && filteredContacts.length > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', pt: 1.5, pb: 0.5, px: 0.5 }}>
                    {filteredContacts.length} {t('contacts').toLowerCase()}
                </Typography>
            )}

            {!loading && filteredContacts.length === 0 && (
                <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 8, color: 'rgba(255,255,255,0.4)' }}>
                    <InboxIcon sx={{ fontSize: 48 }} />
                    <Typography variant="body1">{t('noContactsFound', 'No contacts found')}</Typography>
                </Stack>
            )}

            <Grid data-testid="list-container" container spacing={1} style={{ marginTop: '10px' }}>
                {filteredContacts.map((contact, idx) => (
                    <Grid data-testid={`record-card-${idx}`} key={contact.id || idx} item xs={12} sm={6} md={4} lg={3}>
                        <ContactCard contact={contact} />
                    </Grid>
                ))}
            </Grid>

        </Stack>
    );
};

const getInitials = (contact) => {
    const first = contact.firstname?.trim()?.[0] ?? '';
    const last = contact.lastname?.trim()?.[0] ?? '';
    const initials = (first + last).toUpperCase();
    return initials.length > 0 ? initials : '?';
};

const getAvatarColor = (name) => {
    const colors = ['#76A9AC', '#AC7676', '#7676AC', '#AC9F76', '#76AC82', '#AC76A3'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const ContactCard = ({ contact }) => {
    const navigate = useNavigate();
    const handleClick = () => navigate("/app/contacts/detailed", { state: { contact: contact } });
    const handleKeyDown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } };

    const name = getContactName(contact);
    const initials = getInitials(contact);
    const avatarColor = getAvatarColor(name);

    return (
        <Box
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            aria-label={name}
            sx={{
                ...glassCard,
                cursor: 'pointer',
                transition: 'transform 0.15s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${ACCENT_GLOW}` },
                '&:active': { transform: 'scale(0.98)' },
                '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' }, '&:active': { transform: 'none' } },
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    backgroundColor: avatarColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, mt: 0.25,
                }}>
                    <Typography sx={{ color: '#0a1929', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1 }}>
                        {initials}
                    </Typography>
                </Box>

                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: 'white', lineHeight: 1.3 }} noWrap>
                        {name}
                    </Typography>
                    {contact.email && (
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <MailOutlined sx={{ fontSize: 14, color: ACCENT, flexShrink: 0 }} />
                            <Typography noWrap sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)' }}>
                                {contact.email}
                            </Typography>
                        </Stack>
                    )}
                    {contact.phone && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <PhoneOutlined sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}>
                                {contact.phone}
                            </Typography>
                        </Stack>
                    )}
                    {contact.company?.name && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <WorkOutlineOutlined sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }} noWrap>
                                {contact.company.name}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}

const DetailedInfoContact = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [contact, setContact] = useState(state?.contact ?? null);
    const [loading, setLoading] = useState(!state?.contact);
    const [showRecorder, setShowRecorder] = useState(false);
    const [recordMode, setRecordMode] = useState(null); // null | 'email'
    const [processing, setProcessing] = useState(false);
    const [configuredProps, setConfiguredProps] = useState([]);

    const fetchContact = useCallback(() => {
        if (!contact?.id) return;
        api.fetchContact(contact.id).then((response) => {
            setContact(prev => prev ? { ...prev, ...response } : response);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [contact?.id]);

    useEffect(() => {
        if (!state?.contact) {
            navigate('/app/contacts', { replace: true });
            return;
        }
        fetchContact();
        api.fetchProperties('contact').then((props) => {
            setConfiguredProps(Array.isArray(props) ? props : []);
        }).catch(() => {});
    }, []);

    const handleRecordFinished = (audio) => {
        setProcessing(true);
        const jsonObject = buildContactRecordJson(contact, recordMode);
        api.UploadAudio(audio).then((response) => {
            setProcessing(false);
            setRecordMode(null);
            const mode = recordMode === null ? 'edit-contact' : null;
            navigate('/app/validate', { state: { text: response.text, json: jsonObject, mode, routeBack: '/app/contacts' } });
        }).catch((error) => {
            setProcessing(false);
            setRecordMode(null);
            Sentry.captureException(error);
        });
    };

    if (!contact && !loading) return null;

    // Loading skeleton
    if (!contact && loading) {
        return (
            <Stack sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: SURFACE_HOVER }} />
                    <Skeleton variant="text" width="30%" sx={{ bgcolor: SURFACE_HOVER }} />
                </Stack>
                <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                    <Skeleton variant="circular" width={80} height={80} sx={{ bgcolor: SURFACE_HOVER }} />
                    <Skeleton variant="text" width="50%" sx={{ bgcolor: SURFACE_HOVER, fontSize: TYPE_SCALE['2xl'] }} />
                    <Skeleton variant="text" width="30%" sx={{ bgcolor: SURFACE_HOVER }} />
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                    <Skeleton variant="rounded" width="50%" height={48} sx={{ borderRadius: '16px', bgcolor: SURFACE_HOVER }} />
                    <Skeleton variant="rounded" width="50%" height={48} sx={{ borderRadius: '16px', bgcolor: SURFACE_HOVER }} />
                </Stack>
                <Skeleton variant="rounded" width="100%" height={160} sx={{ borderRadius: '16px', bgcolor: SURFACE_HOVER, mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: '14px', bgcolor: SURFACE_HOVER }} />
            </Stack>
        );
    }

    const name = getContactName(contact);
    const initials = getInitials(contact);
    const avatarColor = getAvatarColor(name);
    const company = contact.company;

    return (
        <Stack sx={{ p: { xs: 2, sm: 3 } }} spacing={2.5}>
            {/* Header */}
            <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton
                    data-testid="contact-detail-back"
                    aria-label={t('goBack', 'Go back')}
                    onClick={() => navigate('/app/contacts')}
                    sx={{ color: TEXT_SECONDARY }}
                >
                    <ArrowBack />
                </IconButton>
                <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('contact')}
                </Typography>
            </Stack>

            {/* Avatar Hero */}
            <Stack alignItems="center" spacing={1}>
                <Box
                    data-testid="contact-avatar"
                    sx={{
                        width: 80, height: 80, borderRadius: '50%',
                        backgroundColor: avatarColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 4px 20px ${ACCENT_GLOW}`,
                    }}
                >
                    <Typography sx={{ color: '#0a1929', fontWeight: 700, fontSize: '1.6rem', lineHeight: 1 }}>
                        {initials}
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: TYPE_SCALE['2xl'], fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.2, textAlign: 'center' }}>
                    {name}
                </Typography>
                {contact.createdate && (
                    <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY }}>
                        {dayjs(contact.createdate).format('L')}
                    </Typography>
                )}
            </Stack>

            {/* Quick Action Row */}
            <Stack direction="row" spacing={1.5}>
                <Button
                    data-testid="contact-email-action"
                    disabled={!contact.email}
                    aria-label={t('sendEmail')}
                    onClick={() => navigate('/app/validate', { state: { text: '', json: { mail: { recipient: [contact], subject: '', message: '' } } } })}
                    sx={{
                        ...glassCard,
                        flex: 1, height: 48,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 1, textTransform: 'none', textDecoration: 'none',
                        color: contact.email ? ACCENT : TEXT_HINT,
                        fontSize: TYPE_SCALE.md, fontWeight: 500,
                        transition: 'background-color 200ms ease-out',
                        '&:hover': contact.email ? { bgcolor: SURFACE_HOVER } : {},
                        '&.Mui-disabled': { color: TEXT_HINT, borderColor: 'rgba(255,255,255,0.03)' },
                        ...noMotion
                    }}
                >
                    <MailOutlined sx={{ fontSize: 20 }} />
                    {t('sendEmail')}
                </Button>
                <Button
                    data-testid="contact-call-action"
                    component="a"
                    href={contact.phone ? `tel:${contact.phone}` : undefined}
                    disabled={!contact.phone}
                    aria-label={t('callContact')}
                    sx={{
                        ...glassCard,
                        flex: 1, height: 48,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 1, textTransform: 'none', textDecoration: 'none',
                        color: contact.phone ? ACCENT : TEXT_HINT,
                        fontSize: TYPE_SCALE.md, fontWeight: 500,
                        transition: 'background-color 200ms ease-out',
                        '&:hover': contact.phone ? { bgcolor: SURFACE_HOVER } : {},
                        '&.Mui-disabled': { color: TEXT_HINT, borderColor: 'rgba(255,255,255,0.03)' },
                        ...noMotion
                    }}
                >
                    <PhoneOutlined sx={{ fontSize: 20 }} />
                    {t('callContact')}
                </Button>
            </Stack>

            {/* Voice Update Button */}
            <Button
                data-testid="contact-voice-update"
                aria-label={t('voiceUpdate')}
                variant="outlined"
                startIcon={<MicIcon />}
                onClick={() => { setRecordMode(null); setShowRecorder(prev => !prev); }}
                fullWidth
                sx={{
                    borderColor: ACCENT, color: ACCENT, fontWeight: 600,
                    borderRadius: '14px', height: 48, textTransform: 'none',
                    fontSize: TYPE_SCALE.md,
                    '&:hover': { bgcolor: `${ACCENT}10`, borderColor: ACCENT },
                    ...noMotion
                }}
            >
                {t('voiceUpdate')}
            </Button>

            {/* Recorder Section */}
            {showRecorder && (
                <Stack spacing={1}>
                    {recordMode === null && (
                        <Stack direction="row" justifyContent="center">
                            <FlowContextChip type="contact" entity={contact} />
                        </Stack>
                    )}
                    <Box sx={{
                        ...glassCard,
                        overflow: 'hidden',
                        p: 0,
                        display: 'flex',
                        justifyContent: 'center',
                    }}>
                        <Box
                            aria-live="polite"
                            sx={{
                                transform: 'scale(0.55)',
                                transformOrigin: 'top center',
                                mt: 7,
                                mb: -11,
                                width: '182%',
                            }}
                        >
                            {!processing && (
                                <Recorder
                                    finished={handleRecordFinished}
                                    onClear={() => {}}
                                    onStart={() => {}}
                                    small={true}
                                    audioText={recordMode === 'email' ? t('audioEmailText') : t('audioContactText')}
                                />
                            )}
                            {processing && <ProcessingComponent processingText={t('processingAudio')} />}
                        </Box>
                    </Box>
                </Stack>
            )}

            {/* Contact Info Card */}
            <Box data-testid="contact-info-card" sx={{ ...glassCard }}>
                <Stack spacing={2}>
                    <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('contactInfo')}
                    </Typography>

                    {/* Email row */}
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: `${ACCENT}15`
                        }}>
                            <MailOutlined sx={{ color: ACCENT, fontSize: 22 }} />
                        </Box>
                        <Stack>
                            <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('email')}
                            </Typography>
                            <Typography sx={{ fontSize: TYPE_SCALE.md, fontWeight: 600, color: TEXT_PRIMARY }}>
                                {contact.email || '—'}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Divider sx={{ borderColor: SURFACE_BORDER }} />

                    {/* Phone row */}
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: `${ACCENT}15`
                        }}>
                            <PhoneOutlined sx={{ color: ACCENT, fontSize: 22 }} />
                        </Box>
                        <Stack>
                            <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('phone')}
                            </Typography>
                            <Typography sx={{ fontSize: TYPE_SCALE.md, fontWeight: 600, color: TEXT_PRIMARY }}>
                                {contact.phone || '—'}
                            </Typography>
                        </Stack>
                    </Stack>

                    {contact.createdate && (
                        <>
                            <Divider sx={{ borderColor: SURFACE_BORDER }} />

                            {/* Create date row */}
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: '12px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: `${ACCENT}15`
                                }}>
                                    <CalendarMonthIcon sx={{ color: ACCENT, fontSize: 22 }} />
                                </Box>
                                <Stack>
                                    <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {t('createdate')}
                                    </Typography>
                                    <Typography sx={{ fontSize: TYPE_SCALE.md, fontWeight: 600, color: TEXT_PRIMARY }}>
                                        {dayjs(contact.createdate).format('L')}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </>
                    )}
                </Stack>
            </Box>

            {/* Associated Company Card */}
            {company && (
                <Box
                    data-testid="contact-company-card"
                    role="button"
                    tabIndex={0}
                    aria-label={`${t('company')}: ${company.name || ''}`}
                    onClick={() => navigate('/app/companies/detailed', { state: { company } })}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/app/companies/detailed', { state: { company } }); } }}
                    sx={{
                        ...glassCard,
                        cursor: 'pointer',
                        transition: 'background-color 200ms ease-out, border-color 200ms ease-out',
                        '&:hover': { bgcolor: SURFACE_HOVER, borderColor: `${ACCENT}30` },
                        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                        ...noMotion
                    }}
                >
                    <Stack spacing={1.5}>
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('company')}
                        </Typography>
                        <Typography sx={{ fontSize: TYPE_SCALE.lg, fontWeight: 600, color: TEXT_PRIMARY }}>
                            {company.name || '—'}
                        </Typography>
                        {company.domain && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <WebIcon sx={{ fontSize: 16, color: TEXT_HINT }} />
                                <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY }}>{company.domain}</Typography>
                            </Stack>
                        )}
                        {getAddressCompany(company, t) !== t('address') && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PlaceOutlined sx={{ fontSize: 16, color: TEXT_HINT }} />
                                <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY }}>{getAddressCompany(company, t)}</Typography>
                            </Stack>
                        )}
                        <Stack direction="row" justifyContent="flex-end">
                            <ChevronRightIcon sx={{ color: TEXT_HINT, fontSize: 20 }} />
                        </Stack>
                    </Stack>
                </Box>
            )}

            {/* Configured Properties */}
            {configuredProps.length > 0 && contact.configuredProperties && (
                <Box data-testid="configured-properties-card" sx={{ ...glassCard }}>
                    <Stack spacing={2}>
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('additionalDetails')}
                        </Typography>
                        {configuredProps
                            .filter(prop => contact.configuredProperties?.[prop.name] != null)
                            .map((prop, idx, arr) => (
                                <React.Fragment key={prop.name}>
                                    {idx > 0 && <Divider sx={{ borderColor: SURFACE_BORDER }} />}
                                    <Stack>
                                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {prop.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: TYPE_SCALE.md, fontWeight: 600, color: TEXT_PRIMARY }}>
                                            {formatPropertyValue(prop, contact.configuredProperties[prop.name])}
                                        </Typography>
                                    </Stack>
                                </React.Fragment>
                            ))
                        }
                    </Stack>
                </Box>
            )}
        </Stack>
    );
}

export const getContactName = (contact) => {
    const first = contact.firstname?.trim() ?? '';
    const last = contact.lastname?.trim() ?? '';
    const name = `${first} ${last}`.trim();
    return name.length > 0 ? name : '--';
}

export const getPropertiesContact = (contact, t) => {
    const properties = [
        {icon: <MailOutlined />, label: t('email'), value: contact.email},
        {icon: <PhoneOutlined />, label: t('phone'), value: contact.phone}
    ]

    if (contact.company != null) {
        properties.push({icon: <WorkOutlineOutlined />, label: t('company'), value: contact.company.name});
        properties.push({icon: <WebIcon />, label: t('domain'), value: contact.company.domain});
        properties.push({icon: <PlaceOutlined />, label: t('address'), value: getAddressCompany(contact.company, t)});
    }

    return properties;
}

const formatPropertyValue = (prop, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (prop.type === 'date') return dayjs(value).format('L');
    if (prop.type === 'datetime') return dayjs(value).format('L LT');
    if (prop.type === 'enumeration' && prop.options?.length > 0) {
        const values = String(value).split(';');
        return values.map(v => prop.options.find(o => o.value === v)?.label || v).join(', ');
    }
    if (prop.type === 'number') return Number(value).toLocaleString();
    return String(value);
};

export function buildContactRecordJson(contact, recordMode) {
    if (recordMode === 'email') {
        return { mail: { recipient: [contact] } };
    }
    const jsonObject = { contact };
    if (contact.company) jsonObject.company = contact.company;
    return jsonObject;
}

const getAddressCompany = (company, t) => {
    const address = [];
    if (company.address) address.push(company.address);
    if (company.city) address.push(company.city);
    if (company.country) address.push(company.country);
    if (address.length === 0) return t('address');
    return address.join(", ");
}

export default { Contacts, ContactCard, DetailedInfoContact };
